import Grid from '@mui/material/Grid';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  findPettyCashDifferenceByPaymentTypeRequested,
  reportCollectionsFilterStartBusinessDateSelector,
  reportCollectionsFilterEndBusinessDateSelector,
  reportFilterCountryCodeSelector,
  reportPettyCashDifferenceByPaymentTypeLoadingSelector,
} from '../state';

import {
  CountryFilter,
  DifferenceTypeFilter,
  PaymentTypesFilter,
  ReportActionsGridItem,
  ReportFilterActions,
  ReportFilters,
  StoreFilter,
} from './ReportFilters';

import { AppPermissions } from '../types';
import { BusinessDateRangeFilter } from './CollectionsFilters';
import { PermissionsProvider } from './Permissions';
import { useOpStructureValidation } from '../hooks';
import { FilterPanel, StatusChip } from './Visual';

const PettyCashDifferenceByPaymentTypeSearch = () => {
  const { t } = useTranslation();
  const validateAndExecuteAction = useOpStructureValidation();
  const startDate = useSelector(reportCollectionsFilterStartBusinessDateSelector);
  const endDate = useSelector(reportCollectionsFilterEndBusinessDateSelector);
  const hasCountry = Boolean(useSelector(reportFilterCountryCodeSelector));
  const applyDisabled = !(startDate.accept && endDate.accept && hasCountry);
  const countryRequiredIncomplete = applyDisabled && !hasCountry;
  const businessDateRequiredIncomplete = applyDisabled && !(startDate.accept && endDate.accept);

  const validatedApplyAction = useCallback(
    () =>
      validateAndExecuteAction(stores =>
        findPettyCashDifferenceByPaymentTypeRequested(false, stores)
      ),
    [validateAndExecuteAction]
  );

  const validatedOnedriveAction = useCallback(
    () =>
      validateAndExecuteAction(stores =>
        findPettyCashDifferenceByPaymentTypeRequested(true, stores)
      ),
    [validateAndExecuteAction]
  );
  return (
    <ReportFilters
      loadingSelector={reportPettyCashDifferenceByPaymentTypeLoadingSelector}
      applyAction={validatedApplyAction}
      onedriveAction={validatedOnedriveAction}
      showOpStructureFilters
    >
      <PermissionsProvider resourcePermissions={[AppPermissions.SALES_TOTALS]}>
        <FilterPanel
          subtitleTranslationKey="petty-cash-difference-by-payment-type.filters.subtitle"
          actions={
            <StatusChip
              status={applyDisabled ? 'warning' : 'success'}
              label={
                applyDisabled ? t('filters.status.missingRequired') : t('filters.status.ready')
              }
            />
          }
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <CountryFilter fullWidth requiredIncomplete={countryRequiredIncomplete} />
            </Grid>
            <Grid item xs={12} md={3} lg={3} xl={2}>
              <StoreFilter />
            </Grid>
            <Grid item xs={12} md={6} lg={5} xl={4}>
              <BusinessDateRangeFilter
                spacing={1}
                requiredIncomplete={businessDateRequiredIncomplete}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={2} xl={2}>
              <DifferenceTypeFilter fullWidth />
            </Grid>
            <Grid item xs={12} md={4} lg={2} xl={2}>
              <PaymentTypesFilter />
            </Grid>
            <ReportActionsGridItem>
              <ReportFilterActions applyDisabled={applyDisabled} />
            </ReportActionsGridItem>
          </Grid>
        </FilterPanel>
      </PermissionsProvider>
    </ReportFilters>
  );
};

export default PettyCashDifferenceByPaymentTypeSearch;
